<script setup lang="ts">
import { computed, ref, watch } from 'vue';

export interface Props {
  src: string;
  alt?: string;
  fallbackSrc?: string;
  eager?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  eager: false,
});

const error = ref(false);
const isLoading = ref(false);
const currentSrc = ref(props.src);

const getSrc = computed(() => currentSrc.value);

const loadingMode = computed(() => (props.eager ? 'eager' : 'lazy'));
const onImageLoad = () => {
  isLoading.value = false;
};

const onImageError = async () => {
  if (props.fallbackSrc && currentSrc.value !== props.fallbackSrc) {
    currentSrc.value = props.fallbackSrc;
    error.value = false;
    isLoading.value = true;
  } else {
    error.value = true;
    isLoading.value = false;
  }
};

const onImageLoadStart = () => {
  isLoading.value = true;
};

watch(
  () => props.src,
  (newSrc) => {
    currentSrc.value = newSrc;
    error.value = false;
    isLoading.value = true;
  },
);
</script>

<template>
  <div class="backstage-image-container" :class="{ 'is-loading': isLoading, 'has-error': error }">
    <div v-if="isLoading" class="image-loading" data-cy="image-loading">
      <div class="loader"></div>
    </div>
    <div v-else-if="error" class="image-placeholder" data-cy="image-placeholder">
      <div class="placeholder-icon">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M21 19V5C21 3.9 20.1 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 19 20.1 21 19ZM8.5 13.5L11 16.51L14.5 12L19 18H5L8.5 13.5Z"
            fill="currentColor" />
        </svg>
      </div>
      <span class="placeholder-text">{{ $t('global.image_error') }}</span>
    </div>
    <img
      v-else
      :loading="loadingMode"
      :src="getSrc"
      :alt="alt"
      decoding="async"
      v-bind="$attrs"
      @load="onImageLoad"
      @error="onImageError"
      @loadstart="onImageLoadStart"
      data-cy="product-image" />
  </div>
</template>

<style scoped lang="scss">
.backstage-image-container {
  position: relative;
  display: inline-block;
  width: 100%;
  height: 100%;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: opacity 0.3s ease;

    &:not([src]) {
      opacity: 0;
    }
  }

  .image-loading {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary-text-color);
    border-radius: 4px;
    z-index: 1;
  }

  .image-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    min-height: 120px;
    border-radius: 4px;
    color: var(--secondary-text-color, #6c757d);
    text-align: center;

    .placeholder-icon {
      margin-bottom: 0.5rem;
      opacity: 0.6;
    }

    .placeholder-text {
      font-size: 0.875rem;
      max-width: 100%;
      word-wrap: break-word;
    }
  }

  &.is-loading img {
    opacity: 0.5;
  }

  &.has-error {
    .image-placeholder {
      color: var(--primary-text-color);
    }
  }
}

@media (max-width: 768px) {
  .backstage-image-container {
    .image-placeholder {
      min-height: 80px;
      padding: 0.5rem;

      .placeholder-text {
        font-size: 0.75rem;
      }
    }
  }
}
</style>
